import React, { useEffect, useState } from "react";
import { FaExternalLinkAlt, FaLink, FaMagic, FaUnlink } from "react-icons/fa";
import { Link } from "react-router-dom";
import SocialPlatformIcon from "../../../../components/SocialPlatformIcon";
import { socialPlatformLabel } from "../../../../constants/socialPlatforms";

const NEW_ACCOUNT = "new";

/** Setup profile step -> Social Accounts platform (services/social_profile_sync.py STEP_PROFILES). */
export const STEP_PLATFORM = {
  fb_page: "facebook",
  instagram: "instagram",
  youtube: "youtube",
  linkedin_page: "linkedin",
  x_profile: "twitter",
  gbp: "gmb",
  brand_gmail: "gmail",
};

export function socialAccountHref(isStaff, tenantId, accountId) {
  if (!isStaff) return accountId ? `/portal/social-accounts/${accountId}` : "/portal/social-accounts";
  if (!tenantId) return null;
  return accountId ? `/tenants/${tenantId}/social-accounts/${accountId}/view` : `/tenants/${tenantId}/view`;
}

function accountLine(account) {
  return [account.Username, account.Profile_URL].filter(Boolean).join(" · ");
}

function optionLabel(o, suggestedId) {
  const parts = [o.Account_Name, o.Username].filter(Boolean).join(" · ");
  const platform = socialPlatformLabel(o.Platform_Name) || o.Platform_Name;
  return `${parts}${platform ? ` (${platform})` : ""}${o.Account_Id === suggestedId ? " · suggested" : ""}`;
}

/**
 * Drawer: the Social Account this profile step is linked to. Linked fields (login, URL, password)
 * are then read from and saved to that record only; the step keeps its own extra fields.
 * Unlinked: pick a record (the matching one is suggested) or create one from the step's values.
 */
export function SocialLinkCard({ step, isStaff, tenantId, canEdit, busy, fieldLabel, onLink, onUse }) {
  const match = step.Social_Match;
  const linkedId = step.Social_Account_Id;
  const options = step.Social_Options || [];
  const suggestedId = !linkedId && match?.Account ? match.Account.Account_Id : null;
  const [picking, setPicking] = useState(!linkedId);
  const [choice, setChoice] = useState(suggestedId ? String(suggestedId) : "");

  useEffect(() => {
    setPicking(!linkedId);
    setChoice(suggestedId ? String(suggestedId) : "");
  }, [linkedId, suggestedId]);

  if (!(step.Linkable_Fields || []).length && !match) return null;
  const account = linkedId ? options.find((o) => o.Account_Id === linkedId) || match?.Account : match?.Account;
  const suggested = match?.Suggested || {};
  const entries = Object.entries(suggested);
  const href = linkedId ? socialAccountHref(isStaff, tenantId, linkedId) : null;
  const linkedLabels = (step.Linkable_Fields || []).map((k) => fieldLabel(k) || k).join(", ");

  return (
    <div className={`dms-social${linkedId ? " is-linked" : ""}`}>
      <div className="dms-social__head">
        <span className="dms-social__icon">
          <SocialPlatformIcon platform={account?.Platform_Name || STEP_PLATFORM[step.Code]} size={16} />
        </span>
        <span className="dms-social__main">
          <strong>
            {linkedId
              ? `Linked to Social Account: ${account?.Account_Name || "account"}`
              : "Not linked to a Social Account"}
          </strong>
          <span className="dmp-muted">
            {linkedId
              ? `${(account && accountLine(account)) || "No username yet"} · ${linkedLabels} saved there`
              : `Link one to keep ${linkedLabels || "the login"} only in Social Accounts.`}
          </span>
        </span>
        {href ? (
          <Link className="dmp-btn dmp-btn--ghost dmp-btn--sm" to={href}>
            Open <FaExternalLinkAlt aria-hidden="true" />
          </Link>
        ) : null}
      </div>

      {canEdit && linkedId && !picking ? (
        <div className="dms-social__actions">
          <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" disabled={busy} onClick={() => setPicking(true)}>
            <FaLink aria-hidden="true" /> Change
          </button>
          <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" disabled={busy} onClick={() => onLink(null)}>
            <FaUnlink aria-hidden="true" /> Unlink
          </button>
        </div>
      ) : null}

      {canEdit && picking ? (
        <div className="dms-social__pick">
          <label className="dmp-field">
            <span>Social Account</span>
            <select value={choice} disabled={busy} onChange={(e) => setChoice(e.target.value)}>
              <option value="">Choose…</option>
              {options.some((o) => o.Fits) ? (
                <optgroup label="Same platform">
                  {options.filter((o) => o.Fits).map((o) => (
                    <option key={o.Account_Id} value={o.Account_Id}>
                      {optionLabel(o, suggestedId)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {options.some((o) => !o.Fits) ? (
                <optgroup label="Other Social Accounts">
                  {options.filter((o) => !o.Fits).map((o) => (
                    <option key={o.Account_Id} value={o.Account_Id}>
                      {optionLabel(o, suggestedId)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              <option value={NEW_ACCOUNT}>+ Create new from this step&apos;s details</option>
            </select>
          </label>
          <span className="dms-social__pick-actions">
            {linkedId ? (
              <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" disabled={busy} onClick={() => setPicking(false)}>
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              className="dmp-btn dmp-btn--sm"
              disabled={busy || !choice || String(linkedId) === choice}
              onClick={() => onLink(choice === NEW_ACCOUNT ? NEW_ACCOUNT : Number(choice))}
            >
              <FaLink aria-hidden="true" /> {choice === NEW_ACCOUNT ? "Create & link" : "Link"}
            </button>
          </span>
        </div>
      ) : null}

      {entries.length ? (
        <div className="dms-social__fill">
          <ul className="dms-social__list">
            {entries.map(([key, value]) => (
              <li key={key}>
                <span className="dmp-muted">{fieldLabel(key) || key}:</span> {String(value)}
              </li>
            ))}
          </ul>
          <button type="button" className="dmp-btn dmp-btn--sm" onClick={() => onUse(suggested)}>
            <FaMagic aria-hidden="true" /> Use for empty fields
          </button>
        </div>
      ) : null}
    </div>
  );
}